<template>
  <b-container>
    <h1 v-translate>Company Area</h1>
    <hr>
    <div class="row">
      <div class="col-md-3">
        <company-navigation />
      </div>
      <div class="col-md-9 personal-info">
        <b-row>
          <b-col cols="12" class="mb-4">
            <b-alert :show="dismissCountDown" dismissible variant="success" @dismissed="dismissCountDown=0" @dismiss-count-down="countDownChanged">
              <p v-translate>The notification was updated!</p>
            </b-alert>
            <h3 v-translate>System Notifications</h3>
            <b-alert show variant="warning">
              <h3 v-translate>Important!</h3>
              <p v-translate>All the configurations and customizations contained in this page are <strong>sensitive</strong>, requires some level of acknowledge on the use of HTML and/or Markdown syntax, and could <strong>make your notifications break or don't work as intended in a snap</strong>.</p>
              <p><strong v-translate>Please, be very careful and don't make any change if you don't really know what are you doing.</strong></p>
            </b-alert>
            <p class="mt-4 mb-1" v-translate>Here you can enable/disable/customize the notifications that our system send on different situations.</p>
          </b-col>
        </b-row>
        <div v-if="notificationTypes.length > 0">
          <b-card v-for="(notificationType, index) in notificationTypes" :key="index" v-if="notificationType.companyNotificationConfig">
            <form @submit.prevent="setCompanyNotificationConfig(notificationType.companyNotificationConfig)">
              <p class="mb-1" v-translate="{notificationTypeName: notificationType.name, notificationTypeDescription: notificationType.description}">Key: <strong>%{notificationTypeName}</strong> <em>(%{notificationTypeDescription})</em></p>
              <p class="mb-1" v-translate="{notificationTypeMedia: notificationType.media_default}">Type: <strong>%{notificationTypeMedia}</strong></p>
              <b-form-checkbox v-model="notificationType.companyNotificationConfig.enabled" :value="1" :unchecked-value="0" class="mb-3">
                <translate>Enabled</translate>
              </b-form-checkbox>
              <div v-if="Boolean(defaultNotifications.find(x => x.id === notificationType.id))">
                <h5><translate>Template</translate></h5>
                <b-form-radio-group v-model="notificationType.companyNotificationConfig.custom" class="pb-3">
                  <b-form-radio :value="false"><translate>Default</translate></b-form-radio>
                  <b-form-radio :value="true"><translate>Custom</translate></b-form-radio>
                </b-form-radio-group>
                <div v-if="notificationType.companyNotificationConfig.custom === true" style="margin: 0 -20px;">
                  <b-tabs card>
                    <b-tab active>
                      <template #title>
                        <translate>Template Edit</translate>
                      </template>
                      <b-row>
                        <b-col>
                          <b-form-group>
                            <label for="" v-translate>Subject</label>
                            <b-form-input v-model="notificationType.companyNotificationConfig.subject"
                              type="text"
                              :id="'notification-subject' + index"
                            ></b-form-input>
                            <b-form-text v-translate>Plain text only. You may use the available variables.</b-form-text>
                          </b-form-group>
                          <b-form-group>
                            <label for="" v-translate>Body</label>
                            <b-form-textarea rows="9" :id="'body' + index" :ref="'body' + index" v-model="notificationType.companyNotificationConfig.template"></b-form-textarea>
                            <b-form-text v-translate>You may use both Markdown and HTML syntax. You may use the available variables too.</b-form-text>
                          </b-form-group>
                        </b-col>
                        <b-col cols="4">
                          <h6><translate>Variables Reference</translate> <b-button style="padding: 0" pill variant="outline-primary" size="sm"><strong style="padding: .1rem .37rem" :id="'tooltip-vars-reference' + index">?</strong></b-button></h6>
                          <b-tooltip :target="'tooltip-vars-reference' + index">
                            <translate>You may use one or more of the following variables/placeholders (click to copy to the clipboard) which will be replaced by real data when the notifications goes out.</translate>
                          </b-tooltip>
                          <dl>
                            <div v-for="placeholder in placeholders" :key="placeholder.name" v-if="(!placeholder.avoidIn || (placeholder.avoidIn.length > 0 && placeholder.avoidIn.indexOf(notificationType.name) === -1)) && (!placeholder.restrictTo || (placeholder.restrictTo.length > 0 && placeholder.restrictTo.indexOf(notificationType.name) !== -1))">
                              <dt><code @click="copyToClipboard(placeholder.name.replace('\n', ''), $gettext('code'), true)" style="cursor: pointer">{{ placeholder.name }}</code></dt>
                              <dd><small>{{ placeholder.description }}</small></dd>
                            </div>
                          </dl>
                        </b-col>
                      </b-row>
                      <b-row>
                        <b-col v-if="notificationType.companyNotificationConfig.custom === true">
                          <b-button size="sm" variant="secondary" @click="sendTestEmail(notificationType.companyNotificationConfig.subject || defaultNotifications.find(x => x.id === notificationType.id).subject, notificationType.companyNotificationConfig.template)" :disabled="!notificationType.companyNotificationConfig.template" v-translate>
                            Send Test Email
                          </b-button>
                          <b-form-text v-translate>This send an email to your email address with dummy data to test how your changes will look.</b-form-text>
                        </b-col>
                      </b-row>
                    </b-tab>

                    <b-tab :disabled="!notificationType.companyNotificationConfig.subject && !notificationType.companyNotificationConfig.template">
                      <template #title>
                        <translate>Template Preview</translate>
                      </template>
                      <h5 v-translate>Subject</h5>
                      <p>{{notificationType.companyNotificationConfig.subject || defaultNotifications.find(x => x.id === notificationType.id).subject}}</p>
                      <hr>
                      <h5 v-translate>Body</h5>
                      <vue-markdown :watches="['notificationType.companyNotificationConfig.template']" :source="$options.filters.cleanUpHtml(notificationType.companyNotificationConfig.template)" v-if="notificationType.companyNotificationConfig.template"></vue-markdown>
                      <div v-else v-html="defaultNotifications.find(x => x.id === notificationType.id).body"></div>
                    </b-tab>
                  </b-tabs>
                </div>
                <div v-else class="pb-5">
                  <b-button size="sm" variant="primary" @click="getDefaultNotificationPreview(notificationType.id)">
                    <translate>Preview Default Template</translate>
                  </b-button>
                </div>
              </div>
              <div v-else>
                <b-alert variant="info" show>
                  <translate>Not customizable.</translate>
                </b-alert>
              </div>
              <div class="text-center">
                <b-button variant="primary" type="submit" v-translate>
                  Update Notification
                </b-button>
              </div>
            </form>
          </b-card>
        </div>

        <b-modal size="xl" id="modalSystemNotificationPreview" ref="modalSystemNotificationPreview" :hide-footer="true" @hide="systemNotificationPreview = {}">
          <div slot="modal-title" v-translate>System Notification Preview</div>
          <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
          <h5 v-translate>Subject</h5>
          <p>{{systemNotificationPreview.subject}}</p>
          <hr>
          <h5 v-translate>Body</h5>
          <b-card>
            <div v-html="systemNotificationPreview.body"></div>
          </b-card>
        </b-modal>
      </div>
    </div>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { FETCH_PROFILE } from '@/store/actions.type'
import CompanyNavigation from '@/components/CompanyNavigation'
import VueMarkdown from 'vue-markdown-v2'

export default {
  name: 'SystemNotificationsPage',
  title() {
    return this.$gettext('System Notifications')
  },
  components: {
    CompanyNavigation,
    VueMarkdown
  },
  data() {
    return {
      dismissSecs: 10,
      dismissCountDown: 0,
      defaultNotifications: [],
      systemNotificationPreview: {},
      companyNotificationConfigs: [],
      baseConfig: {
        enabled: 1,
        media: 'email',
        subject: null,
        template: '',
        company_id: null,
        notification_type_id: null,
        custom: false
      }
    }
  },
  computed: {
    hasCompany() {
      return this.$store.getters.profile && this.$store.getters.profile.company_id !== null
    },
    isCompanyAdmin() {
      return this.hasCompany && this.$store.getters.profile.company && this.$store.getters.profile.roles.findIndex(x => x.name === 'company-admin') !== -1
    },
    placeholders() {
      return [
        {
          name: '{{$shippingId}}',
          description: this.$gettextInterpolate(this.$gettext('The %{siteName} ID of the shipping.'), { siteName: this.siteName })
        },
        {
          name: '{{$internalCode}}',
          description: this.$gettext('Your Company internal shipping code/reference.')
        },
        {
          name: '{{$url}}',
          description: this.$gettext('The URL to the shipping page for the receiver.')
        },
        {
          name: '{{$companyName}}',
          description: this.$gettext('Your Company commercial name/brand.')
        },
        {
          name: '{{$companyLogo}}',
          description: this.$gettext('The URL to your Company logo. (Available only if you uploaded it into the Company Profile.)')
        },
        {
          name: '{{$receiverName}}',
          description: this.$gettext('The receiver\'s name.')
        },
        {
          name: '{{$shippingItems}}',
          description: this.$gettext('Render a table with the shipping items and their quantities.')
        },
        {
          name: '{{address}}',
          description: this.$gettext('The destination address.'),
          avoidIn: ['wrong_address']
        },
        {
          name: '{{delivery_time_from}}',
          description: this.$gettext('Estimated time from which the shipment can be delivered.'),
          restrictTo: ['close_to_arrive']
        },
        {
          name: '{{delivery_time_to}}',
          description: this.$gettext('Estimated time to which the shipment can be delivered.'),
          restrictTo: ['close_to_arrive']
        }
      ]
    },
    ...mapGetters([
      'user'
    ])
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.$store.dispatch(FETCH_PROFILE)
        .then(response => {
          this.$store.commit('setEmail', this.user.email)
          const payload = { profile: this.user }
          this.$store.commit('setProfile', payload)

          this.companyNotificationConfigs = this.$store.getters.profile.company.notification_configs
          this.companyNotificationConfigs = this.companyNotificationConfigs.map((x, i) => {
            this.$set(this.companyNotificationConfigs[i], 'custom', (x.subject !== null || x.template !== null))
            return x
          })
          this.fetchNotificationTypes()
            .then(response => {
              this.notificationTypes = this.notificationTypes.map((x, i) => {
                let companyNotificationConfig
                if (this.companyNotificationConfigs.findIndex(y => y.notification_type_id === x.id && y.media === 'email') !== -1) {
                  companyNotificationConfig = this.companyNotificationConfigs.find(y => y.notification_type_id === x.id && y.media === 'email')
                } else {
                  companyNotificationConfig = { ...this.baseConfig }
                  companyNotificationConfig.notification_type_id = x.id
                  companyNotificationConfig.company_id = this.$store.getters.profile.company_id
                  companyNotificationConfig.media = 'email'
                }
                this.$set(this.notificationTypes[i], 'companyNotificationConfig', companyNotificationConfig)
                return x
              })
              this.notificationTypes.forEach((notification, i) => {
                this.getDefaultNotification(notification.id)
              })
            })
        })
    },
    getDefaultNotification(id) {
      this.$http.get(this.$appConfig.api.baseUrl + 'shippings/notification-types/' + id + '/template')
        .then(response => {
          const theNotification = response.body
          theNotification.id = id
          this.defaultNotifications.push(theNotification)
        }, error => {
          console.log(error)
          this.$root.$children[0].$refs.simplert.justCloseSimplert()
        })
    },
    getDefaultNotificationPreview(id) {
      this.systemNotificationPreview = this.defaultNotifications.find(x => x.id === id)
      this.$refs.modalSystemNotificationPreview.show()
    },
    setCompanyNotificationConfig(config) {
      config.subject = config.subject || null
      config.template = config.template ? this.$options.filters.cleanUpHtml(config.template) : null
      if (config.custom === false) {
        config.subject = null
        config.template = null
      }
      if (config.id) {
        this.$http.patch(this.$appConfig.api.baseUrl + 'company-notification-configs/' + config.id, config)
          .then(response => {
            this.init()
            this.showAlert()
          })
      } else {
        this.$http.post(this.$appConfig.api.baseUrl + 'company-notification-configs', config)
          .then(response => {
            this.init()
            this.showAlert()
          })
      }
    },
    sendTestEmail(subject, body) {
      const payload = {
        subject,
        body
      }
      this.$http.post(this.$appConfig.api.baseUrl + 'notifications/emails/test', payload)
        .then(response => {
          const obj = {
            title: '',
            message: this.$gettext('A test email has been sent to your user email address'),
            type: 'info',
            customCloseBtnText: this.$gettext('Close')
          }
          this.$root.$children[0].$refs.simplert.openSimplert(obj)
        })
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs
    }
  }
}
</script>
